import { ReactNode } from "react";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  message?: string;
}

/**
 * Simple loading spinner component for navigation guards and loading states
 */
export function LoadingSpinner({ size = "md", className = "", message }: LoadingSpinnerProps): ReactNode {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
  };

  return (
    <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
      <div
        className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-gray-200 border-t-gray-600`}
        role="status"
        aria-label="Loading"
      />
      {message && <p className="mt-2 text-sm text-gray-600">{message}</p>}
    </div>
  );
}

/**
 * Full page loading spinner for major navigation transitions
 */
export function PageLoadingSpinner({ message = "Loading..." }: { message?: string }): ReactNode {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <LoadingSpinner size="lg" message={message} />
    </div>
  );
}

/**
 * Inline loading spinner for smaller components
 */
export function InlineLoadingSpinner({ message }: { message?: string }): ReactNode {
  return (
    <div className="flex items-center justify-center py-4">
      <LoadingSpinner size="sm" message={message} />
    </div>
  );
}
